import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Tasks from './pages/Tasks';
import Puantaj from './pages/Puantaj';
import DailyReports from './pages/DailyReports';
import Photos from './pages/Photos';
import Users from './pages/Users';
import Teams from './pages/Teams';
import Resources from './pages/Resources';
import Reports from './pages/Reports';
import AdminPermissions from './pages/AdminPermissions';
import Checklists from './pages/Checklists';
import FirstPasswordSetup from './pages/FirstPasswordSetup';
import Settings from './pages/Settings';
import Accounting from './pages/Accounting';
import ChangeOrders from './pages/ChangeOrders';
import OperationsCenter from './pages/OperationsCenter';
import Nonconformities from './pages/Nonconformities';
import {
  ROLE_IDS,
  canAccessAccountingPanel,
  canAccessAdminPermissions,
  canManageNotificationPreferences,
  canManageUserDirectory,
  hasEffectivePermission,
  isAccountant,
} from './utils/accessControl';
import './App.css';

const FullscreenLoading = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '1.2rem', color: '#666' }}>
    Yükleniyor...
  </div>
);

const ProtectedRoute = ({ children, isAuthenticated, loading }) => {
  if (loading) {
    return <FullscreenLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AccessRoute = ({ children, allowed, fallback = '/' }) => {
  if (!allowed) {
    return <Navigate to={fallback} replace />;
  }
  return children;
};

const NonAccountantRoute = ({ children, user }) => {
  if (isAccountant(user)) {
    return <Navigate to="/accounting" replace />;
  }
  return children;
};

const AppShellRoute = ({ children, isAuthenticated, loading, user }) => {
  if (loading) {
    return <FullscreenLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.must_change_password) {
    return <Navigate to="/first-password" replace />;
  }

  return children;
};

function App() {
  const { isAuthenticated, loading, user, permissions, permissionsLoading } = useAuth();

  const appLoading = loading || permissionsLoading;
  const accountantUser = isAccountant(user);
  const roleId = Number(user?.role_id || 0);
  const canSeeUsers = canManageUserDirectory(user, permissions);
  const canSeeAccounting = canAccessAccountingPanel(user, permissions);
  const canSeeAdminPermissions = canAccessAdminPermissions(user);
  const canSeeReports = !accountantUser && hasEffectivePermission(user, permissions, 'can_view_reports');
  const canSeeTeams = !accountantUser && (
    hasEffectivePermission(user, permissions, 'can_manage_teams') ||
    [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER, ROLE_IDS.SITE_SUPERVISOR].includes(roleId)
  );
  const canSeeResources = !accountantUser && (
    hasEffectivePermission(user, permissions, 'can_manage_resources') ||
    [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER, ROLE_IDS.SITE_SUPERVISOR].includes(roleId)
  );

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated
            ? <Navigate to={user?.must_change_password ? '/first-password' : '/'} replace />
            : <Login />
        }
      />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/" replace /> : <Register />} />
      <Route
        path="/first-password"
        element={(
          <ProtectedRoute isAuthenticated={isAuthenticated} loading={appLoading}>
            {user?.must_change_password ? <FirstPasswordSetup /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        )}
      />

      <Route
        path="/"
        element={(
          <AppShellRoute isAuthenticated={isAuthenticated} loading={appLoading} user={user}>
            <Layout />
          </AppShellRoute>
        )}
      >
        <Route
          index
          element={accountantUser ? <Navigate to="/accounting" replace /> : <Dashboard />}
        />
        <Route path="projects" element={<NonAccountantRoute user={user}><Projects /></NonAccountantRoute>} />
        <Route path="projects/:id" element={<NonAccountantRoute user={user}><ProjectDetail /></NonAccountantRoute>} />
        <Route path="tasks" element={<NonAccountantRoute user={user}><Tasks /></NonAccountantRoute>} />
        <Route path="projects/:projectId/tasks" element={<NonAccountantRoute user={user}><Tasks /></NonAccountantRoute>} />
        <Route path="puantaj" element={<NonAccountantRoute user={user}><Puantaj /></NonAccountantRoute>} />
        <Route path="daily-reports" element={<NonAccountantRoute user={user}><DailyReports /></NonAccountantRoute>} />
        <Route path="photos" element={<NonAccountantRoute user={user}><Photos /></NonAccountantRoute>} />
        <Route
          path="users"
          element={(
            <AccessRoute allowed={canSeeUsers} fallback={accountantUser ? '/accounting' : '/'}>
              <Users />
            </AccessRoute>
          )}
        />
        <Route
          path="teams"
          element={(
            <AccessRoute allowed={canSeeTeams} fallback={accountantUser ? '/accounting' : '/'}>
              <Teams />
            </AccessRoute>
          )}
        />
        <Route
          path="resources"
          element={(
            <AccessRoute allowed={canSeeResources} fallback={accountantUser ? '/accounting' : '/'}>
              <Resources />
            </AccessRoute>
          )}
        />
        <Route path="checklists" element={<NonAccountantRoute user={user}><Checklists /></NonAccountantRoute>} />
        <Route
          path="reports"
          element={(
            <AccessRoute allowed={canSeeReports} fallback={accountantUser ? '/accounting' : '/'}>
              <Reports />
            </AccessRoute>
          )}
        />
        <Route path="change-orders" element={<NonAccountantRoute user={user}><ChangeOrders /></NonAccountantRoute>} />
        <Route path="nonconformities" element={<NonAccountantRoute user={user}><Nonconformities /></NonAccountantRoute>} />
        <Route path="operations-center" element={<NonAccountantRoute user={user}><OperationsCenter /></NonAccountantRoute>} />
        <Route
          path="accounting"
          element={(
            <AccessRoute allowed={canSeeAccounting} fallback="/">
              <Accounting />
            </AccessRoute>
          )}
        />
        <Route path="settings" element={<Settings />} />
        <Route
          path="notification-preferences"
          element={(
            <AccessRoute allowed={canManageNotificationPreferences(user)} fallback="/">
              <Navigate to="/settings" replace />
            </AccessRoute>
          )}
        />
        <Route
          path="admin-permissions"
          element={(
            <AccessRoute allowed={canSeeAdminPermissions} fallback="/">
              <AdminPermissions />
            </AccessRoute>
          )}
        />
      </Route>

      <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />} />
    </Routes>
  );
}

export default App;
